import { useSnackbar } from 'notistack';
import { Fade, useTheme } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Condominium, PersonProfile } from '../../../../entities';
import useStyles from './styles';
import ProfilePanel from './components/ProfilePanel';
import { AddButton, EditableBloc, IconTitle, VerticalLine } from '../../../../components';
import NoContent from './components/NoContent';
import FlexContainer from '../../../../components/FlexContainer';
import { keyBy } from '../../../../utils/objects';
import ProfileRole from '../../../../entities/profile-role';
import { useCondominiums, useFetch, useProfiles } from '../../../../hooks';
import AlreadyExistsProfileError from '../../../../errors/profiles.error';
import ProfileStatus from '../../../../entities/profile-status';

const roleNameMapper: Record<ProfileRole, string> = {
  [ProfileRole.Employee]: 'Funcionário',
  [ProfileRole.Unknown]: 'Morador',
  [ProfileRole.Tenant]: 'Inquilino',
  [ProfileRole.Dweller]: 'Morador',
  [ProfileRole.Guest]: 'Visitante',
  [ProfileRole.Owner]: 'Proprietário',
  [ProfileRole.Manager]: 'Manager',
};

export const Profiles = (): JSX.Element => {
  const styles = useStyles();
  const { spacing } = useTheme();
  const [profiles, setProfiles] = useState<PersonProfile[]>([]);
  const { id: personId } = useParams<{ id: string }>();
  const repository = useProfiles(personId!);
  const { enqueueSnackbar } = useSnackbar();
  const [isPanelVisible, setIsPanelVisible] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<PersonProfile>({} as PersonProfile);
  const [condominiums, setCondominiums] = useState<Condominium[]>([]);
  const [condominiumsMapper, setCondominiumsMapper] = useState<Record<string, Condominium>>(
    {} as Record<string, Condominium>
  );
  const condominiumsRepository = useCondominiums();
  const { data: condominiumsResponse } = useFetch(condominiumsRepository.fetchAll);
  const { data, refetch, loading } = useFetch(repository.fetchAll);

  const [inProgress, setInProgress] = useState(false);

  useEffect(() => {
    setCondominiumsMapper(keyBy(condominiumsResponse, 'id'));
    setCondominiums(condominiumsResponse ?? []);
  }, [condominiumsResponse]);

  useEffect(() => {
    const dataFiltered = data?.filter((e) => !e.deleted);

    setProfiles(dataFiltered ?? []);
  }, [data]);

  const handleSetPanelOpen = () => {
    setSelectedProfile({} as PersonProfile);
    setIsPanelVisible(!isPanelVisible);
  };

  const verifyIfProfileAlreadyExists = async (profile: PersonProfile) => {
    const result = profiles.find((ref) => ref.condominiumId === profile.condominiumId && ref.role === profile.role);

    if (result) {
      throw new AlreadyExistsProfileError();
    }
  };

  const handleSubmit = async (profile: PersonProfile) => {
    try {
      await verifyIfProfileAlreadyExists(profile);

      if (profile.id) {
        await repository.update(profile);
      } else {
        await repository.create(profile);
      }

      enqueueSnackbar('Perfil salvo com sucesso!', { variant: 'success' });
      refetch();
      handleSetPanelOpen();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Não foi possível salvar o perfil';

      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  const handleSelectProfile = (profile?: PersonProfile) => {
    if (profile) {
      setSelectedProfile(profile);
    }

    setIsPanelVisible(true);
  };

  const handleDeleteProfile = async (profile: PersonProfile) => {
    setInProgress(true);
    try {
      await repository.deleteProfile(profile.id);
      enqueueSnackbar('Perfil deletado com sucesso!', { variant: 'success' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Não foi deletar o perfil';
      enqueueSnackbar(message, { variant: 'error' });
    }
    setIsPanelVisible(false);
    setInProgress(false);
    refetch();
  };

  const showNoContent = useMemo(() => !isPanelVisible && profiles.length === 0, [isPanelVisible, profiles]);

  if (loading && profiles.length === 0) {
    return <></>;
  }

  return (
    <div className={styles.container}>
      {showNoContent ? (
        <NoContent />
      ) : (
        <div className={styles.containerRight}>
          <IconTitle title="Lista de perfis" />
          <FlexContainer>
            {profiles.map((profile) => (
              <EditableBloc<PersonProfile>
                label={roleNameMapper[profile.role]}
                width={`calc(33.3% - ${spacing(1.33)})`}
                maxWidth="600px"
                minWidth="300px"
                value={profile}
                status={profile.status === ProfileStatus.Disabled ? 'Desativado' : 'Ativo'}
                key={profile.id}
                onClickEdit={handleSelectProfile}
              >
                <div className={styles.info}>
                  <div>
                    Condominio:{' '}
                    {profile.condominiumId && condominiumsMapper[profile.condominiumId]
                      ? condominiumsMapper[profile.condominiumId].name
                      : 'SEM CONDOMINIO'}
                  </div>
                </div>
              </EditableBloc>
            ))}
          </FlexContainer>
        </div>
      )}
      <Fade in={isPanelVisible} mountOnEnter unmountOnExit>
        <VerticalLine />
      </Fade>
      <ProfilePanel
        condominiums={condominiums}
        visible={isPanelVisible}
        profile={selectedProfile}
        personId={personId}
        onClickClose={handleSetPanelOpen}
        onSubmit={handleSubmit}
        onClickDelete={handleDeleteProfile}
        inProgress={inProgress}
      />

      {!isPanelVisible && <AddButton label="adicionar perfil" onClick={() => handleSetPanelOpen()} />}
    </div>
  );
};

export default Profiles;
